/**
 * Runner for scraping and uploading inventory data
 */
import Common from './common'
import Env from './env'
import Logger from './logger'
import TrackingPixel from './trackingPixel'

async function run({ advertiserId, client, document, verticalFallback }, { setConfig, getItem }) {
  let context = {
    stage: 'UNKNOWN',
    scraperConfigURL: null,
    inventoryURL: null,
  }

  const scraperConfig = await _fetchScraperConfig({ advertiserId, context })

  if (!scraperConfig) {
    // do not log warnings as the advertiser might not have configured scraper-configuration
    _insertTrackingPixel({ advertiserId, document })
    return
  }

  Common.attempt(
    newConfig => {
      setConfig(newConfig)
    },
    {
      target: 'mapper',
      value: scraperConfig,
      quiet: true,
    }
  )

  const vertical = scraperConfig.vertical || verticalFallback

  // we require vertical in order to update scraped inventory-data
  if (!vertical || String(vertical).trim().length === 0) {
    _reportWarnings({ advertiserId, client, context })
    _insertTrackingPixel({ advertiserId, document })
    return
  }

  // Fetch all the mappable fields and their expected data-type
  //  so that we don't emit product containing invalid field values
  const verticalFields = await _fetchVerticalFields({
    vertical,
    context,
  })

  if (verticalFields) {
    Common.attempt(
      newConfig => {
        setConfig(newConfig)
      },
      {
        target: 'verticalFields',
        value: verticalFields,
        quiet: true,
      }
    )
  }

  Common.attempt(
    newConfig => {
      setConfig(newConfig)
    },
    {
      target: 'vertical',
      value: vertical,
      quiet: true,
    }
  )

  const product = await _scrapeProduct({ getItem, context })
  Logger.info(
    `uploadInventoryRunner.js - _fetchScraperConfig - Fetched product: ${JSON.stringify(product)}`
  )

  if (!product) {
    _reportWarnings({ advertiserId, client, context })
    _insertTrackingPixel({ advertiserId, document })
    return
  }

  const productId = await _emitProduct({
    product,
    advertiserId,
    vertical,
    context,
  })

  _reportWarnings({ advertiserId, client, context, product })
  _insertTrackingPixel({ advertiserId, document, productId })
}

async function _fetchScraperConfig({ advertiserId, context }) {
  context.stage = 'CONFIG_REQUESTING'
  let scraperConfig = null

  try {
    context.scraperConfigURL = `${Env.SCRAPER_CONFIG_URL()}${encodeURIComponent(advertiserId)}`
    Logger.info(
      `uploadInventoryRunner.js - _fetchScraperConfig - Fetching config from: ${context.scraperConfigURL}`
    )
    scraperConfig = await Common.fetchWithTimeout(context.scraperConfigURL)
      .then(raw => {
        // NOT between 200 and 300
        if (!raw.ok) {
          throw 'status=' + raw.status
        }

        return raw.json()
      })
      .then(config => {
        const p = _parseRemoteConfig(config)
        return p
      })
  } catch (err) {
    Logger.warn(`Failed to fetch scraper-config: ${err}`)
  }

  context.stage = 'CONFIG_RECEIVED'
  return scraperConfig
}

async function _fetchVerticalFields({ vertical, context }) {
  context.stage = 'VERTICAL_FIELDS_REQUESTING'
  let verticalFields = null

  try {
    const verticalFieldsUrl = Env.VERTICAL_FIELDS(vertical)

    Logger.info(
      `uploadInventoryRunner.js - _fetchVerticalFields - Fetching vertical-fields from: ${verticalFieldsUrl}`
    )

    verticalFields = await Common.fetchWithTimeout(verticalFieldsUrl).then(raw => {
      // NOT between 200 and 300
      if (!raw.ok) {
        throw 'status=' + raw.status
      }
      return raw.json()
    })
  } catch (err) {
    Logger.warn(`Failed to fetch vertical-fields: ${err}`)
  }

  context.stage = 'VERTICAL_FIELDS_RECEIVED'
  return verticalFields
}

async function _scrapeProduct({ getItem, context }) {
  context.stage = 'PRODUCT_SCRAPPING'
  let product = null

  let attempt = 0
  while (++attempt < 3) {
    product = getItem({ target: 'product' })
    if (product) {
      if (!product.productId) {
        product.productId = Common.generateIdByURL(product.productLink)
      }
      break
    }
    await new Promise(resolve => {
      setTimeout(resolve, 2000)
    })
  }

  if (product) {
    context.stage = 'PRODUCT_SCRAPED'
  } else {
    context.stage = 'PRODUCT_NOT_FOUND'
  }
  return product
}

async function _emitProduct({ product, advertiserId, vertical, context }) {
  context.stage = 'PRODUCT_SENDING'
  let productId = null

  try {
    productId = product.productId

    context.inventoryURL = Env.INVENTORY_POST_URL(vertical) + encodeURIComponent(advertiserId)

    Logger.info(`uploadInventoryRunner.js - emitProduct - Posting to : ${context.inventoryURL}`)
    Logger.info(
      `uploadInventoryRunner.js - emitProduct - with product : ${JSON.stringify(product, null, 3)}`
    )

    await Common.fetchWithTimeout(context.inventoryURL, {
      method: 'POST',
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(Object.assign({}, product, { source: 'scraper' })),
    }).then(raw => {
      // NOT between 200 and 300
      if (!raw.ok) {
        throw 'status=' + raw.status
      }
    })
  } catch (err) {
    Logger.warn(`Failed to post product: ${err}`)
  }

  context.stage = 'PRODUCT_SENT'
  return productId
}

function _insertTrackingPixel({ advertiserId, document, productId }) {
  Common.attempt(
    pixelData => {
      TrackingPixel.insert(pixelData)
    },
    {
      advertiserId,
      document,
      productId,
    }
  )
}

function _parseRemoteConfig(remoteConfig) {
  if (!remoteConfig || !remoteConfig.configurations || remoteConfig.configurations.length === 0) {
    return null
  }

  // convert to a compatible scraper-config that maps from the first configuration object
  return {
    vertical: remoteConfig.vertical,
    ...remoteConfig.configurations[0],
  }
}

async function _reportWarnings({ advertiserId, client, context, product }) {
  try {
    const { stage, inventoryURL, scraperConfigURL } = context

    // attempt to read client-config from remote
    // comment out for now - causing errors in the console due to non-existent endpoint
    // await _trySyncClientConfiguration({ client })

    const traceProbability = (client && client.traceProbability) || 0.1

    if (Logger.getWarnings().length > 0 && Math.random() < traceProbability) {
      const uniqueWarnings = Logger.getWarnings().reduce((acc, cur) => {
        acc.add(cur)
        return acc
      }, new Set())

      const reportURL = Env.ACTIVITY_POST_URL() + encodeURIComponent(advertiserId)
      await Common.fetchWithTimeout(reportURL, {
        method: 'POST',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ts: Date.now(),
          URL: document.URL,
          type: 'WARN',
          warning: {
            stage,
            advertiserId,
            product,
            scraperConfigURL,
            inventoryURL,
            warnings: Array.from(uniqueWarnings),
          },
        }),
      }).then(raw => {
        // NOT between 200 and 300
        if (!raw.ok) {
          throw 'status=' + raw.status
        }
      })
    }
  } catch (err) {
    Logger.warn(`Failed to report activities: ${err}`)
  }
}

// unused - causing errors in the console due to non-existent endpoint
// async function _trySyncClientConfiguration({ client }) {
//   const clientConfigURL = Env.CONFIG_URL()

//   Logger.info(
//     `uploadInventoryRunner.js - _trySyncClientConfiguration - Fetching client-configurations from: ${clientConfigURL}`
//   )

//   try {
//     const latestClient = await Common.fetchWithTimeout(clientConfigURL).then(raw => {
//       // NOT between 200 and 300
//       if (!raw.ok) {
//         throw 'status=' + raw.status
//       }
//       return raw.json()
//     })
//     Object.assign(client, latestClient)
//   } catch (err) {
//     Logger.warn(`Failed to sync with latest client configuration: ${err}`)
//   }
// }

export default {
  run,
}
